// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import { Redirect } from 'react-router';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as React from 'react';
import { PrimaryButton } from 'office-ui-fabric-react';
import clsx from 'clsx';

import history from 'src/history';

import {
    Progress,
    saveUserDeviceAndAccessorySelection,
    selectedAccessorySelector,
    selectedParentSelector,
    updateCreateOrderProgress
} from './../../store/orderCreation';
import AccessoryListItem from './../../components/AccessoryListItem/AccessoryListItem';
import BusySpinner from './../../components/BusySpinner';
import CommonLayout from './../../components/CommonLayout';
import ClaimOrder from './../../components/ClaimOrder';
import DeviceInformation from './../../components/DeviceInformation/DeviceInformation';
import ParentListItem from './../../components/ParentListItem/ParentListItem';
import Warranties from './../../components/Warranties/Warranties';

import { sessionId, logPageAction } from 'src/lib/ApplicationInsights';
import CorrelationVector from 'src/lib/CorrelationVector';
import {
    deviceAccessorySelector,
    deviceInformationSelector,
    deviceSelector,
    deviceWarrantiesSelector,
    accessoryDeviceEligibilitySelector,
    claimDetailSelector
} from './../../store/deviceInfo/selectors';
import { zipCodeSelector } from './../../store/orderCreation/selectors';
import { userAgentTierSelector, selectedOptionSelector } from './../../store/user/selector';
import { actionStatusSelector } from './../../store/actionStatus/selectors';
import { GET_DEVICE_ELIGIBILITY_DATA } from './../../store/deviceInfo/eligibility';
import { GET_BATCH_SERVICE_REQUEST_DATA } from './../../store/deviceInfo/claimOrder';

import { LoadingState } from './../../store/actionStatus';

import { IClaimOrder } from './../../businessLogic/sagas/serviceRequest/types';

import { getUserTierConfig } from './../../lib/userTierConfig';
import { getDeviceInfoData } from './../../store/deviceInfo/deviceInfo';
import Styles from './styles.module.scss';
import { selectedCountryInfoSelector } from './../../store/countries/selectors';
import { putSelectedCountryData } from './../../store/countries';
import { countriesSelector } from './../../components/ServiceRequestAndDeviceLookup/selector';
import ClaimDetails from './../../components/ClaimDetails/ClaimDetails';

interface ListItemProps {
    name: string;
    sku: string;
    serialNumber: string;
}

export default function DeviceDetails() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const device = useSelector(state => deviceSelector(state));
    const zipCodeInfo = useSelector(state => zipCodeSelector(state));
    const countryInfo = useSelector(state => selectedCountryInfoSelector(state));
    const country = countryInfo && countryInfo.countryName;
    const deviceInformation = useSelector(state =>
        deviceInformationSelector(state)
    );
    const selectedViewOption = useSelector(state => selectedOptionSelector(state));

    if (!deviceInformation) {
        return <Redirect to="/" />;
    }

    const warranties = useSelector(state => deviceWarrantiesSelector(state));
    const eligibilityCallMonitor = useSelector(state => actionStatusSelector(state)(GET_DEVICE_ELIGIBILITY_DATA));
    const isEligibilityPending = eligibilityCallMonitor.status === LoadingState.started;
    const accessoryEligibility = useSelector(state => accessoryDeviceEligibilitySelector(state));
    const isAccessoryEligible = accessoryEligibility.toLowerCase() === 'eligible';
    const deviceAccessories = useSelector(state => deviceAccessorySelector(state));
    const selectedParentInStore = useSelector(state => selectedParentSelector(state));
    const [selectedParent, setSelectedParent] = React.useState<ListItemProps[] | []>(selectedParentInStore ? [selectedParentInStore] : []);
    const [selectedAccessory, setSelectedAccessory] = React.useState<ListItemProps[] | []>(useSelector(state => selectedAccessorySelector(state)));
    const [isCreateOrderDisabled, setIsCreateOrderDisabled] = React.useState<boolean>(true);
    const orderHistoryStatus = useSelector(state => actionStatusSelector(state)(GET_BATCH_SERVICE_REQUEST_DATA));
    const orderHistory: IClaimOrder[] = useSelector((state: any) => state && state.device ? state.device.claims : '');
    const registration = useSelector((state: any) => state && state.device ? state.device.registration : '');
    const agentTier = useSelector(state => userAgentTierSelector(state));
    const claimDetails = useSelector(state => claimDetailSelector(state).payload);
    const deviceProps = {
        warranties,
        claimDetails,
        country,
        isCreateOrderView: true,
        announceTitle: true,
        description: deviceInformation.description,
        deviceName: deviceInformation.repairProduct.name,
        image: deviceInformation.repairProduct.imageUri,
        marketPlace: deviceInformation.marketplaceName,
        partNumber: deviceInformation.partNumber,
        resetPath: '/',
        serialNumber: device.serialNumber,
        seriesName: deviceInformation.msPressSeriesName,
        status: device.status,
        zipCode: zipCodeInfo,
        deviceSDTRunDetail: device.deviceSDTRunDetail,
        countryOrigin: device.attributes?.countryOfOrigin,
        color: device.attributes?.color,
    };

    const parentProps: ListItemProps = {
        name: deviceProps.deviceName,
        sku: deviceInformation.partNumber,
        serialNumber: device.serialNumber,
    };

    const accessoryInformationProps: ListItemProps[] = deviceAccessories.map((accessory: any) => ({
        name: accessory.name,
        sku: accessory.symptomCode,
        serialNumber: accessory.serialNumber,
        data: accessory,
    })).sort((accessory1: any, accessory2: any) =>
        accessory1.name.localeCompare(accessory2.name)
    );

    const tierConfig = getUserTierConfig();
    const allCountries = useSelector(state => countriesSelector(state));

    function onParentDeviceSelectionChange(selected: ListItemProps[]) {
        logPageAction('Device selection changed', { itemsSelected: selected });

        setSelectedParent(selected);
        setIsCreateOrderDisabled(!((selected && selected.length > 0) || (selectedAccessory && selectedAccessory.length > 0)));
    }

    function onAccessorySelectionChange(selected: ListItemProps[]) {
        logPageAction('Accessory selection changed', { itemsSelected: selected });

        setSelectedAccessory(selected);
        setIsCreateOrderDisabled(!((selected && selected.length > 0) || (selectedParent && selectedParent.length > 0)));
    }

    function startOrderCreation() {
        logPageAction('Start order process: Create Order button clicked');

        dispatch(
            saveUserDeviceAndAccessorySelection({
                parent: selectedParent[0],
                accessory: selectedAccessory,
            })
        );
        dispatch(
            updateCreateOrderProgress(Progress.ItemSelection)
        );

        history.push('/device/createOrder', { deviceProps });
    }

    React.useEffect(() => {
        if (orderHistoryStatus.status === LoadingState.notStarted && countryInfo && Object.entries(countryInfo).length === 0) {
            const { serialNumber, zipCode } = deviceProps;
            let selectCountryCode: string;

            if (registration && Object.entries(registration).length !== 0) {
                selectCountryCode = registration.countryCode;
            } else {
                if (deviceInformation && deviceInformation.marketplaceCode) {
                    selectCountryCode = deviceInformation.marketplaceCode;
                } else {
                    selectCountryCode = 'US';
                }
            }

            const selectedCountry = selectCountryCode && Array.isArray(allCountries) && allCountries.find((country: any) => {
                return country
                    && (
                        (country.ISOCode && country.ISOCode.toUpperCase().trim() === selectCountryCode.trim())
                        || (country.ISOCountryShortCode && country.ISOCountryShortCode.toUpperCase().trim() === selectCountryCode)
                    );
            });
            const countryData = { countryCode: selectedCountry.ISOCode, countryName: selectedCountry.ISOName, countryShortCode: selectedCountry.ISOCountryShortCode };
            const { countryCode } = countryData;

            dispatch(
                getDeviceInfoData({
                    countryCode,
                    serialNumber,
                    zipCode,
                    agentTier,
                })
            );
            dispatch(putSelectedCountryData(countryData));
        }
    }, [countryInfo]);

    function renderOrderHistory() {
        switch (orderHistoryStatus.status) {
            case LoadingState.rejected:
                return <label>{t('something_went_wrong', { sessionId, operationId: CorrelationVector.getPreviousCV() })}</label>;
            case LoadingState.notStarted:
            case LoadingState.success:
                const orders = orderHistory.map((order, key) => {
                    return (
                        <div key={key} className={Styles.orderHistory}>
                            <ClaimOrder data={order} />
                        </div>
                    );
                });
                return orders.length ? orders : (
                    <div className={Styles.emptyOrderMessage}>{t('no_orders_found')}</div>
                );
            case LoadingState.started:
            default:
                return <BusySpinner messageKey={t('getting_history')} />;
        }
    }

    const skipLinks = [
        {
            key: 'warranties',
            text: t('warranties'),
        },
        {
            key: 'product_and_accessories',
            text: t('product_and_accessories'),
        },
        {
            key: 'order_history',
            text: t('order_history'),
        },
    ];
    return (
        <CommonLayout
            pageTitle={[t('device_detail')]}
            skipLinks={skipLinks}
        >
            <div className={Styles.bodyContainer}>
                <div className={Styles.sectionContainer}>
                    <DeviceInformation {...deviceProps} />
                </div>
                <div id="warranties" className={Styles.sectionContainer}>
                    <Warranties {...deviceProps} />
                </div>
                {
                    claimDetails && claimDetails.length > 0 ?
                        <div id="claimDetails" className={Styles.sectionContainer}>
                            <ClaimDetails claims={claimDetails} />
                        </div>
                        : null
                }
                {
                    warranties && warranties.length ? <div>
                        <div id="product_and_accessories" className={Styles.sectionContainer}>
                            <h2 className={Styles.label}>
                                {t('product_and_accessories')}
                            </h2>
                            {tierConfig.showReplaceDevice
                                ? <div className={Styles.tableContainer}>
                                    <ParentListItem
                                        selected={selectedParent}
                                        data={[parentProps]}
                                        onSelect={onParentDeviceSelectionChange}
                                    />
                                </div> : null
                            }
                            {(tierConfig.showReplaceAccessory && selectedViewOption !== 'create_a_commercial_exchange_order') ?
                                <div className={Styles.tableContainer}>
                                    <AccessoryListItem
                                        isEligible={isEligibilityPending ? true : isAccessoryEligible}
                                        eligibilityToolTip={accessoryEligibility}
                                        data={accessoryInformationProps}
                                        isLoading={isEligibilityPending}
                                        selected={selectedAccessory}
                                        onSelect={onAccessorySelectionChange}
                                    />
                                </div> : null
                            }
                            <PrimaryButton
                                allowDisabledFocus={true}
                                text={t('start_order')}
                                className={clsx(Styles.createOrderButton, {
                                    [Styles.disabled]: isCreateOrderDisabled,
                                })}
                                disabled={isCreateOrderDisabled}
                                onClick={startOrderCreation}
                            />
                        </div>
                        {tierConfig.orderStatus
                            ? <div id="order_history" className={Styles.sectionContainer}>
                                <h2 className={Styles.label}>
                                    {t('order_history')}
                                </h2>
                                {renderOrderHistory()}
                            </div> : null
                        }
                    </div> : null}
            </div>
        </CommonLayout>
    );
}
